/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { createContext, useContext } from "react";

import {
  EditPriceModal,
  IEditedCartItemDetails,
} from "../../../components/modals/EditPriceModal/EditPriceModal";
import { useAppDispatch } from "../../../store/rootStore";
import { CartItem } from "../../cart/storage/cart.types";
import { editPrice, revertPrice } from "../storage/seedCartThunk";

const SeedEditPriceModalContext = createContext<
  | {
      setOpen: React.Dispatch<React.SetStateAction<boolean>>;
      setItem: React.Dispatch<React.SetStateAction<CartItem | undefined>>;
      setAccountNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
    }
  | undefined
>(undefined);

export const useSeedEditPriceModal = () => {
  const context = useContext(SeedEditPriceModalContext);
  if (!context) {
    throw new Error("useSeedEditPriceModal must be used within a SeedEditPriceProvider");
  }
  return context;
};

type SeedEditPriceProviderProps = {
  children?: React.ReactNode;
};

export const SeedEditPriceProvider: React.FC<SeedEditPriceProviderProps> = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const [item, setItem] = React.useState<CartItem>();
  const [accountNumber, setAccountNumber] = React.useState<string>();

  const dispatch = useAppDispatch();

  const value = React.useMemo(() => {
    return { setOpen, setItem, setAccountNumber };
  }, [setOpen, setItem, setAccountNumber]);

  const onApplyEditedPrice = ({
    editedPrice,
    editedPriceReasonCode,
    editedPriceReasonNote,
    editedQuantity,
    item,
  }: IEditedCartItemDetails) => {
    if (accountNumber) {
      dispatch(
        editPrice({
          item,
          newPrice: editedPrice,
          newQuantity: editedQuantity,
          accountNumber,
          editedPriceReasonCode,
          editedPriceReasonNote,
        })
      );
    }
  };

  const onRevertEditedPrice = (item: CartItem) => {
    if (accountNumber) {
      dispatch(revertPrice({ item, accountNumber }));
    }
  };

  return (
    <SeedEditPriceModalContext.Provider value={value}>
      {children}

      {item && (
        <EditPriceModal
          open={open}
          setOpen={setOpen}
          item={item}
          cartProductCardProps={{
            enableQuantity: false,
            enableRemove: false,
            enableVariants: false,
          }}
          onApplyEditedPrice={onApplyEditedPrice}
          onRevertEditedPrice={onRevertEditedPrice}
        />
      )}
    </SeedEditPriceModalContext.Provider>
  );
};

export default SeedEditPriceProvider;
